import React, { useState, useEffect } from 'react';
import { useAuth, UserProfile } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

interface ProfileFormProps {
  onComplete?: () => void;
}

const ProfileForm: React.FC<ProfileFormProps> = ({ onComplete }) => {
  const { user, profile, updateProfile } = useAuth();
  const [username, setUsername] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  
  // Cargar datos del perfil existente (si hay)
  useEffect(() => {
    if (profile) {
      setUsername(profile.username || '');
      setAvatarUrl(profile.avatar_url || '');
      setPreviewUrl(profile.avatar_url || null);
    }
  }, [profile]);

  // Manejar la selección de archivos para la foto de perfil
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validar tipo y tamaño
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 2 * 1024 * 1024; // 2MB
    
    if (!validTypes.includes(file.type)) {
      setError('Formato de imagen no válido. Use JPG, PNG, GIF o WebP.');
      return;
    }
    
    if (file.size > maxSize) {
      setError('La imagen es demasiado grande. Máximo 2MB permitido.');
      return;
    }
    
    setError(null);
    setAvatarFile(file);
    
    // Crear URL para previsualización
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    
    // Limpiar URL al desmontar componente
    return () => URL.revokeObjectURL(objectUrl);
  };

  // Subir avatar a Supabase Storage
  const uploadAvatar = async (file: File): Promise<string> => {
    setUploading(true);
    try {
      if (!user) throw new Error('Usuario no autenticado');
      
      // Crear nombre de archivo único
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;
      
      // Subir archivo a Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });
      
      if (uploadError) throw uploadError;
      
      // Obtener URL pública
      const { data } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);
      
      return data.publicUrl;
    } catch (error: any) {
      console.error('Error al subir avatar:', error.message);
      throw new Error(`Error al subir imagen: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  // Manejar envío del formulario
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    
    try {
      // Validar campos
      if (!username.trim()) {
        throw new Error('Por favor, ingrese un nombre de usuario');
      }
      
      let finalAvatarUrl = avatarUrl;
      
      // Si hay un nuevo archivo, subirlo
      if (avatarFile) {
        finalAvatarUrl = await uploadAvatar(avatarFile);
      }
      
      // Actualizar perfil
      await updateProfile({
        username: username.trim(),
        avatar_url: finalAvatarUrl,
      });
      
      // Llamar al callback de completado si existe
      if (onComplete) {
        onComplete();
      }
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-gray-900/90 to-gray-800/80 backdrop-blur-sm rounded-xl border border-gray-700/30 p-6 max-w-xl mx-auto">
      <h2 className="text-2xl font-semibold text-white mb-6 flex items-center">
        <svg className="w-6 h-6 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
        Configurar Perfil
      </h2>
      
      {error && (
        <div className="mb-6 bg-red-900/30 border border-red-700/50 text-red-300 px-4 py-3 rounded-lg flex items-center">
          <svg className="w-5 h-5 mr-2 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{error}</span>
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        {/* Avatar Preview & Upload */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-300 mb-2">Foto de Perfil</label>
          <div className="flex items-center">
            <div className="relative mr-4">
              <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-800/70 flex items-center justify-center border border-gray-700/50">
                {previewUrl ? (
                  <img 
                    src={previewUrl} 
                    alt="Avatar Preview" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <svg className="w-12 h-12 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                )}
              </div>
              {uploading && (
                <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                  <svg className="animate-spin h-8 w-8 text-blue-400" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                </div>
              )}
            </div>
            
            <div className="flex-1">
              <div className="relative group">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500/30 to-purple-500/30 rounded-xl blur opacity-0 group-hover:opacity-100 transition duration-300"></div>
                <div className="relative">
                  <label className="w-full flex items-center justify-center px-4 py-2 bg-gray-800/90 border border-gray-700/50 rounded-lg cursor-pointer hover:bg-gray-800 transition-all">
                    <svg className="w-5 h-5 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-sm text-gray-300">Seleccionar imagen</span>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handleFileChange}
                      disabled={uploading}
                    />
                  </label>
                </div>
              </div>
              <p className="mt-2 text-xs text-gray-400">
                JPG, PNG o GIF. Máximo 2MB.
              </p>
            </div>
          </div>
        </div>
        
        {/* Username Field */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-300 mb-2">Nombre de usuario</label>
          <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500/30 to-purple-500/30 rounded-xl blur opacity-0 group-hover:opacity-100 transition duration-300"></div>
            <div className="relative">
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full bg-gray-800/90 border border-gray-700/50 rounded-xl px-5 py-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all duration-300"
                placeholder="Tu nombre o nickname"
                required
              />
            </div>
          </div>
          <p className="mt-2 text-xs text-gray-400">
            Este nombre se mostrará en el chat y otras partes de la aplicación.
          </p>
        </div>
        
        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading || uploading}
            className="px-6 py-3 rounded-xl bg-gradient-to-r from-blue-600/80 to-blue-500/80 text-white hover:from-blue-500/80 hover:to-blue-400/80 shadow-blue-600/20 flex items-center justify-center space-x-2 disabled:opacity-50 shadow-lg transition-all duration-300"
          >
            {loading || uploading ? (
              <>
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span>Guardando...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Guardar Perfil</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProfileForm; 